import { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SiteNav, SiteFooter, PageHero } from "@/components/SiteChrome";
import WorldMap from "@/components/WorldMap";

const ease = [0.22, 1, 0.36, 1];

export default function Company() {
  const nav = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-white">
      <SiteNav />
      <PageHero eyebrow="Our Company"
        title="The Regional Partner Built On Trust."
        sub="Exon Global is the primary regional partner headquartered in the Greater Toronto Area — the technical interface and sales/distribution arm for Ratna Gears Pvt. Ltd. and other manufacturers." />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-14">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease }} className="lg:col-span-7">
          <h2 className="font-head font-extrabold tracked uppercase text-2xl lg:text-3xl text-[#0A192F] leading-tight">35+ Years Manufacturing Heritage</h2>
          <p className="text-slate-600 mt-5 leading-relaxed">We source, verify and deliver mission-critical components for oil & gas, defence, agriculture, heavy industry and steel & mining sinter plants — anchored in trust, governed with transparency. Cost-effective, high-volume production with precision tolerances, backed by decades of supply to rigorous US, Middle Eastern and African markets.</p>
          <div className="mt-8 space-y-3">
            {["ISO 9001 certified manufacturing partners", "Direct factory pricing, no hidden layers", "Single accountable point of contact", "On-the-ground GTA sales & technical support"].map((t) => (
              <div key={t} className="flex items-center gap-3 text-[#0A192F]/80"><CheckCircle2 size={18} className="text-[#D4AF37] shrink-0" /> {t}</div>
            ))}
          </div>
          <button onClick={() => nav("/", { state: { scrollTo: "quote" } })} className="btn-gold mt-9 inline-flex items-center gap-2 font-head font-bold text-sm tracked-sm uppercase px-8 py-4 rounded-lg">
            Request Consultation <ArrowRight size={18} />
          </button>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease, delay: 0.1 }} className="lg:col-span-5 grid grid-cols-2 gap-5 self-start">
          {[["Oil & Gas", "Mission-critical drives"], ["Defence", "Strategic engineering"], ["Agriculture", "Rugged transmission"], ["Steel & Mining", "Sinter plant spares"]].map(([k, v], i) => (
            <div key={i} className="border border-slate-100 rounded-xl p-6 shadow-card">
              <div className="font-head text-[11px] tracked uppercase text-[#D4AF37]">{k}</div>
              <div className="font-head font-bold text-[#0A192F] mt-2">{v}</div>
            </div>
          ))}
        </motion.div>
      </section>

      <section className="bg-white py-16 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">Our Mission</span>
          <p className="font-head font-bold text-[#0A192F] text-xl lg:text-2xl leading-[1.4] tracking-tight mt-6">
            Committed to connecting world-class engineering solutions with industry through
            <span className="gold-text"> precision, trust, and enduring global partnerships</span> — creating
            sustainable value through reliable products, responsive support and long-term relationships.
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">Global Footprint</span>
            <h2 className="font-head font-extrabold tracked uppercase text-2xl lg:text-3xl text-[#0A192F] mt-4">Clients Across Six Regions</h2>
          </div>
          <WorldMap />
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
